import type { IconName } from "@/components/ui/Icon";
import { t } from "@/lib/strings";
import type { MemoAuditEntry } from "@/lib/types";

/** A rendered change-log line: an icon, a German phrase (the actor is shown
 * separately), and — where relevant — the target label and/or an old→new diff. */
export interface MemoAuditLine {
  icon: IconName;
  phrase: string;
  label?: string;
  from?: string;
  to?: string;
}

function str(detail: MemoAuditEntry["detail"], key: string): string | undefined {
  const v = detail?.[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** "Abschnitt › Zwischenabschnitt" for one side of a move, or undefined when the row does not
 * record that side. An EMPTY title is still a place (an untitled section) — `str()` would drop
 * it, and with it the whole destination. */
function place(
  detail: MemoAuditEntry["detail"],
  blockKey: string,
  subKey: string,
): string | undefined {
  const block = detail?.[blockKey];
  if (typeof block !== "string") return undefined;
  const sub = detail?.[subKey];
  const head = block || t.memoMoveUntitled;
  return typeof sub === "string" ? `${head} › ${sub || t.memoMoveUntitled}` : head;
}

function fmtDue(iso?: string): string | undefined {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

/** Map a raw audit row to a human line. Unknown actions fall back to the slug so
 * a new action type is never silently dropped. */
export function formatMemoAudit(e: MemoAuditEntry): MemoAuditLine {
  const label = str(e.detail, "label") ?? str(e.detail, "title");
  const from = str(e.detail, "from");
  const to = str(e.detail, "to");
  // An edit that changed only blank lines, line breaks or spaces: the backend records
  // the text once (its from and to snippets would read the same), so name the kind of
  // change instead of showing the same text twice.
  const layoutOnly = e.detail?.layout_only === true;
  switch (e.action) {
    case "entry.added":
      return { icon: "plus", phrase: "hat einen Punkt hinzugefügt", label };
    case "subheading.added":
      return { icon: "plus", phrase: "hat eine Zwischenüberschrift hinzugefügt", label };
    case "entry.edited":
      if (layoutOnly) return { icon: "edit", phrase: t.memoAuditPointLayoutEdited, label };
      return { icon: "edit", phrase: "hat einen Punkt geändert", from, to };
    case "subheading.edited":
      if (layoutOnly) return { icon: "edit", phrase: t.memoAuditSubheadingLayoutEdited, label };
      return { icon: "edit", phrase: "hat eine Zwischenüberschrift geändert", from, to };
    case "entry.deleted":
      return { icon: "trash", phrase: "hat einen Punkt gelöscht", label };
    // The Aufgaben row was deleted on the board. The entry is RETIRED, never
    // removed — but a silent retirement would be indistinguishable from someone
    // clicking "Zurückziehen", so the change log names where it came from.
    case "task.deleted_on_board":
      return { icon: "trash", phrase: "hat die Aufgabe über das Board entfernt", label };
    // Curating a board-sourced update line. The change log is the ONLY place the
    // removed text survives, so it carries the FULL body — and the body is where
    // the backend puts it (detail.body), not in `label`/`title`.
    case "task.update_removed":
      return {
        icon: "trash",
        phrase: "hat eine Update-Zeile entfernt",
        label: str(e.detail, "body"),
      };
    case "task.restored_on_board":
      return { icon: "refresh", phrase: "hat die Aufgabe über das Board wiederhergestellt", label };
    case "subheading.deleted":
      return { icon: "trash", phrase: "hat eine Zwischenüberschrift gelöscht", label };
    case "entry.retired":
      return { icon: "check", phrase: "hat einen Punkt abgeschlossen", label };
    case "entry.unretired":
      return { icon: "refresh", phrase: "hat einen Punkt wieder geöffnet", label };
    case "subheading.retired":
      return { icon: "check", phrase: "hat eine Zwischenüberschrift abgeschlossen", label };
    case "subheading.unretired":
      return { icon: "refresh", phrase: "hat eine Zwischenüberschrift wieder geöffnet", label };
    case "task.due": {
      const due = fmtDue(str(e.detail, "due"));
      return {
        icon: "date",
        phrase: due ? `hat eine Frist gesetzt (bis ${due})` : "hat die Frist entfernt",
        label,
      };
    }
    case "task.completed":
      return {
        icon: "check",
        phrase: "hat eine Aufgabe erledigt",
        label,
        to: str(e.detail, "solution"),
      };
    case "task.reopened":
      return { icon: "refresh", phrase: "hat eine Aufgabe wieder geöffnet", label };
    case "block.added":
      return { icon: "plus", phrase: "hat einen Abschnitt hinzugefügt", label };
    case "block.edited":
      return { icon: "edit", phrase: "hat einen Abschnitt umbenannt", from, to };
    // from/to are places in the order ("Platz 3" → "Platz 2"); a move from a cached
    // client (raw position) carries the section title only.
    case "block.moved":
      return { icon: "moveRight", phrase: "hat einen Abschnitt verschoben", label, from, to };
    // A move names WHERE: "Abschnitt › Zwischenabschnitt" on both sides (the sub-heading part
    // only when it was in / went into one). Rows written before a side was recorded simply
    // lack it — older moves carry `to` only.
    case "entry.moved":
      return {
        icon: "moveRight",
        phrase: "hat einen Punkt verschoben",
        label,
        from: place(e.detail, "from", "from_subheading"),
        to: place(e.detail, "to", "to_subheading"),
      };
    case "subheading.moved":
      return { icon: "moveRight", phrase: "hat eine Zwischenüberschrift verschoben", label, to };
    case "block.deleted":
      return { icon: "trash", phrase: "hat einen Abschnitt gelöscht", label };
    case "header.edited":
      return { icon: "edit", phrase: "hat die Kopfdaten geändert" };
    case "participants.edited":
      return { icon: "person", phrase: "hat die Teilnehmer geändert" };
    case "finalized":
      return { icon: "check", phrase: "hat das Protokoll finalisiert" };
    case "reopened":
      return { icon: "refresh", phrase: "hat das Protokoll wieder geöffnet" };
    default:
      return { icon: "history", phrase: e.action };
  }
}
